import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, AuthState } from '../types/auth';
import { dbHelpers, initializeDatabase } from '../config/database';

export interface LoginResult {
  success: boolean;
  message?: string;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  register: (userData: Omit<User, 'id'>, password: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'x2infotech_token';
const USER_KEY = 'x2infotech_user';

export const resolveUserRole = (userData: any): User['role'] => {
  const rid = Number(userData?.role_id);
  const rname = String(userData?.role_name || userData?.role || '').toLowerCase();
  const dept = String(userData?.department || '').toLowerCase();

  if (rid === 1 || rname === 'admin') return 'admin';
  if (rid === 2 || rname === 'hr' || dept === 'human resources' || dept === 'hr') return 'hr';
  if (rid === 4 || rname === 'product_manager') return 'product_manager';
  if (rid === 5 || rname === 'finance') return 'finance';
  if (rid === 6 || rname === 'scrum_master') return 'scrum_master';
  if (rid === 7 || rname === 'client') return 'client';
  if (rid === 8 || rname === 'sales' || dept === 'sales') return 'sales';
  if (rid === 9 || rname === 'marketing' || dept === 'marketing') return 'marketing';
  return 'employee';
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true
  });

  useEffect(() => {
    async function init() {
      await initializeDatabase();

      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setAuthState({ user: null, isAuthenticated: false, isLoading: false });
        return;
      }

      try {
        const res = await dbHelpers.me(token);
        if (res?.status === true && res?.user) {
          const transformedUser: User = {
            ...res.user,
            role: resolveUserRole(res.user),
            joinDate: res.user.created_at,
            isActive: res.user.is_active === 1,
            approvalStatus: res.user.approval_status || 'approved'
          };
          localStorage.setItem(USER_KEY, JSON.stringify(transformedUser));
          setAuthState({
            user: transformedUser,
            isAuthenticated: true,
            isLoading: false
          });
          return;
        }
      } catch (err) {
        console.warn("Backend /auth_me.php verification error:", err);
      }

      // Preserve existing cached session if server is momentarily unreachable
      const cached = localStorage.getItem(USER_KEY);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.email) {
            setAuthState({ user: parsed, isAuthenticated: true, isLoading: false });
            return;
          }
        } catch (e) {}
      }

      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setAuthState({ user: null, isAuthenticated: false, isLoading: false });
    }

    init();
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    setAuthState(prev => ({ ...prev, isLoading: true }));

    const cleanEmail = email.trim();
    const cleanPass = password.trim();

    try {
      const res = await dbHelpers.login({
        email: cleanEmail,
        password: cleanPass
      });

      console.log("LOGIN API RESPONSE:", res);

      const status = res?.status ?? res?.success;
      const token = res?.token ?? res?.access_token;
      const user = res?.data ?? res?.user;

      if (status === true && token && user) {
        const transformedUser: User = {
          ...user,
          role: resolveUserRole(user),
          joinDate: user.created_at,
          isActive: user.is_active === 1,
          approvalStatus: user.approval_status || 'approved'
        };
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, JSON.stringify(transformedUser));

        setAuthState({
          user: transformedUser,
          isAuthenticated: true,
          isLoading: false
        });

        return { success: true };
      }

      const msg = res?.message || 'Invalid credentials or account not approved yet.';
      setAuthState({ user: null, isAuthenticated: false, isLoading: false });
      return { success: false, message: msg };
    } catch (err: any) {
      console.error("Login request error:", err);
      setAuthState({ user: null, isAuthenticated: false, isLoading: false });
      return { 
        success: false, 
        message: err?.message || 'Unable to connect to authentication server. Please verify backend API status.' 
      };
    }
  };

  const logout = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      try { dbHelpers.me(token); } catch (e) { }
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setAuthState({ user: null, isAuthenticated: false, isLoading: false });
  };

  const register = async (userData: Omit<User, 'id'>, password: string): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    try {
      const payload: any = {
        name: userData.name,
        email: userData.email,
        password,
        role_id: 3,
        department: userData.department || ''
      };
      const res = await dbHelpers.register(payload);
      if (res && res.status) {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return true;
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return false;
      }
    } catch (err) {
      console.error('Register error', err);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{
      ...authState,
      login,
      logout,
      register
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
